'use client'

import { useEffect, useState } from 'react'
import { Gift } from 'lucide-react'
import { cn } from '@/lib/utils'
import { clearReferralCode, readReferralCode, storeReferralCode } from '@/lib/referral-capture'
import { fetchReferralProgramTerms, type ReferralProgramTerms } from '@/lib/referral-program'

/**
 * Canje manual del código de invitación en el SIGNUP (AC2.6 — camino primario
 * de atribución en nativo, donde el `?ref` no llega a la WebView; resuelve el
 * pre-launch gate #7). NO habla con el servidor: escribe el carrier de
 * localStorage que `attemptAccountSync` ya adjunta al único fetch de sync —
 * misma tubería y mismas reglas fail-closed que el `?ref` web (DD7/ADR-013).
 *
 * Copys DINÁMICOS y honestos: la oferta numérica sale de
 * `GET /api/referral/program` (los mismos valores de env que acredita el cron)
 * y SOLO se promete si P>0. Aquí no existe "bonus de bienvenida" (está a 0):
 * jamás prometer minutos por registrarse.
 *
 * name-free por construcción (C1/D7): sin nombre ni identidad de nadie.
 */

interface ReferralCodeFieldTranslations {
  toggle: string
  label: string
  placeholder: string
  /** Oferta dinámica con P del programa (solo se muestra si P>0). */
  formatPurchaseBonusHint: (minutes: number) => string
}

const DEFAULT_TRANSLATIONS: ReferralCodeFieldTranslations = {
  toggle: '¿Te ha invitado un amigo?',
  label: 'Código de invitación',
  placeholder: 'Pega aquí su código',
  formatPurchaseBonusHint: (minutes) =>
    `Con su código te llevas ${minutes} min extra en tu primera compra.`,
}

interface ReferralCodeFieldProps {
  translations?: Partial<ReferralCodeFieldTranslations>
  /** Tema del formulario de auth (las variantes de personaje van sobre fondo oscuro). */
  onDarkBackground?: boolean
}

function isReferralEnabled(): boolean {
  return process.env.NEXT_PUBLIC_REFERRAL_ENABLED === 'true'
}

export function ReferralCodeField({ translations, onDarkBackground = false }: ReferralCodeFieldProps = {}) {
  const t = { ...DEFAULT_TRANSLATIONS, ...translations }
  const enabled = isReferralEnabled()

  const [code, setCode] = useState('')
  const [open, setOpen] = useState(false)
  const [program, setProgram] = useState<ReferralProgramTerms | null>(null)

  useEffect(() => {
    if (!enabled) return
    // Pre-relleno desde el carrier: el ?ref capturado en la landing (web) llega
    // aquí ya persistido; el usuario puede corregirlo o pegarlo a mano (nativo).
    const pending = readReferralCode()
    if (pending) {
      setCode(pending)
      setOpen(true)
    }
    let active = true
    fetchReferralProgramTerms().then((terms) => {
      if (active) setProgram(terms)
    })
    return () => {
      active = false
    }
  }, [enabled])

  if (!enabled) return null

  const handleChange = (raw: string) => {
    setCode(raw)
    const sanitized = raw.trim()
    // El carrier guarda el código saneado; la validación real (fail-closed
    // contra un referralCode existente) es SIEMPRE server-side en el sellado.
    if (sanitized) {
      storeReferralCode(sanitized)
    } else {
      clearReferralCode()
    }
  }

  const purchaseBonusMinutes = program?.refereePurchaseMinutes ?? 0
  const hint = purchaseBonusMinutes > 0 ? t.formatPurchaseBonusHint(purchaseBonusMinutes) : null

  const mutedText = onDarkBackground ? 'text-white/80' : 'text-muted-foreground'

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn('inline-flex items-center gap-1.5 text-sm underline-offset-2 hover:underline', mutedText)}
      >
        <Gift className="size-4" aria-hidden="true" />
        {t.toggle}
      </button>
    )
  }

  return (
    <div className="space-y-1.5">
      <label
        htmlFor="referral-code"
        className={cn('block text-sm font-medium', onDarkBackground ? 'text-white' : 'text-foreground')}
      >
        {t.label}
      </label>
      <input
        id="referral-code"
        type="text"
        value={code}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={t.placeholder}
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        className={cn(
          'w-full rounded-lg border px-3 py-2 font-mono text-sm',
          onDarkBackground
            ? 'border-white/40 bg-white/10 text-white placeholder:text-white/50'
            : 'border-input bg-background text-foreground placeholder:text-muted-foreground'
        )}
      />
      {hint && <p className={cn('text-xs', mutedText)}>{hint}</p>}
    </div>
  )
}
